import React, { useMemo } from 'lib/react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'lib/redux';
import {
  EVCheckbox,
  EVGrid,
  EVTypography,
} from 'lib/components';
import { updateSelectedOtherProducts, updateSelectedProduct } from 'features/order/Order.actions';
import { getProductFromId } from 'features/order/Order.utils';
import { PRODUCT_IDS } from 'constants/AppConstants';
import { THREE_HOUR_DELIVERY_PRODUCT_ID } from './ProductList.constants';
import OrderStyles from './ProductStyles';
import ProductListExpansionPanel from './ProductListExpansionPanel.component';
import ProductListTemplate from './ProductList.template';

function OtherProducts(props) {
  const { name, products, icon } = props;
  const {
    selectedOtherProducts,
    selectedPrimaryProduct,
  } = useSelector((state) => state.order);
  const classes = OrderStyles();
  const dispatch = useDispatch();
  const productList = useMemo(() => products.reduce((list, product) => ({ ...list, [product.ProductID]: product }), {}), [products]);

  const setSelectedOtherProducts = (updatedSelectedOtherProducts) => dispatch(updateSelectedOtherProducts(updatedSelectedOtherProducts));

  const displayCategory = useMemo(() => products.length > 0, [products]);

  // Check if Property Data is selected (mutual exclusivity)
  const isPropertyDataSelected = useMemo(() => Boolean(
    selectedPrimaryProduct && selectedPrimaryProduct.ProductID === PRODUCT_IDS.PropertyData,
  ), [selectedPrimaryProduct]);

  const handleOtherProductsSelect = (product) => {
    const productId = product.ProductID;
    const deliveryProductId = product.WebDeliveryProducts[0].ProductID;
    // Always clear Property Data when any other product is selected (for mutual exclusivity)
    if (isPropertyDataSelected) {
      dispatch(updateSelectedProduct({
        selectedPrimaryProduct: null,
        selectedPrimaryDeliveryProduct: null,
      }));
    }
    const updatedSelectedOtherProducts = { ...selectedOtherProducts };
    if (selectedOtherProducts[productId]) delete updatedSelectedOtherProducts[productId];
    else updatedSelectedOtherProducts[productId] = deliveryProductId;
    setSelectedOtherProducts(updatedSelectedOtherProducts);
  };

  const handleOtherProductsDeliverySelect = (deliveryProductId, productId) => {
    setSelectedOtherProducts({ ...selectedOtherProducts, [productId]: deliveryProductId });
  };

  const anyProductsSelected = (productId) => Boolean(selectedOtherProducts[productId]);

  const threeHourDeliverySelected = (productId) => (selectedOtherProducts[productId] === THREE_HOUR_DELIVERY_PRODUCT_ID);

  const getSelectedProductsMessage = (productDescription, deliveryType) => `${productDescription} with ${deliveryType} Delivery`;

  const getSelectedProducts = () => (
    <EVGrid item xs={12} sm={12} md={12} lg={12} className={classes.subHeader}>
      {Object.keys(selectedOtherProducts).map((productId) => {
        const product = productList[productId];
        return product && (
          <EVTypography key={product.ProductID}>
            {getSelectedProductsMessage(
              product.Description,
              getProductFromId(product.WebDeliveryProducts, selectedOtherProducts[product.ProductID]).Name,
            )}
          </EVTypography>
        );
      })}
    </EVGrid>
  );

  const getProductSelectComponent = (productId, isIncompatible, handleChange) => (
    <EVCheckbox
      id={`products-${productId}`}
      checked={Boolean(selectedOtherProducts[productId])}
      value={productId}
      disabled={isIncompatible || isPropertyDataSelected}
      onChange={handleChange}
    />
  );
  const getSelectedDeliveryProduct = (productId) => selectedOtherProducts[productId];

  const productListComponent = () => (
    <ProductListTemplate
      products={products}
      getSelectedDeliveryProduct={getSelectedDeliveryProduct}
      anyProductsSelected={anyProductsSelected}
      getProductSelectComponent={getProductSelectComponent}
      threeHourDeliverySelected={threeHourDeliverySelected}
      handleProductSelect={handleOtherProductsSelect}
      handleDeliveryProductSelect={handleOtherProductsDeliverySelect}
      isDisabled={isPropertyDataSelected}
    />
  );

  return displayCategory && (
    <ProductListExpansionPanel
      {...{ name, icon }}
      getSelectedProductsSubHeader={getSelectedProducts}
      productList={productListComponent}
    />
  );
}

OtherProducts.propTypes = {
  name: PropTypes.string.isRequired,
  products: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  icon: PropTypes.element.isRequired,
};
export default OtherProducts;
